import React, {useState} from "react";
import strapi from "/scripts/apis/strapi/index.js";
import css from "./index.module.scss";
import DefaultLayout from "layouts/default";
import Button from "@/components/button";
import ProductCard from "@/components/productCard";
import { useEffect, useRef } from "react";
import FloatingTextInput from "@/components/floating-text-input";
import Selector from "@/components/selector";
import RadioButton from "@/components/radioButton";
import { useRouter } from "next/router";
import SearchBar from "@/components/searchBar";
import RequestPromptForm from "forms/requestPrompt";
import { sendDatalayerEvent } from "@/scripts/utils";

export default function Listings({aiModels, tags, initialListings, newestListings}) {
  const [form, setForm] = useState({
    tags: useRef(),
    query: useRef(),
    aiModels: useRef([]),
    promptTypes: useRef([]),
  });
  const router = useRouter();

  const [listings, setListings] = useState(initialListings);
  const [page, setPage] = useState(0); //-1 indicates that this is the last page and not to display a load more button

  //regenerate search results or load more of them
  async function updateSearch(newPage = 0, save = true){
    let aiModel;
    for (let item of form?.aiModels?.current){
      if (item.getValue()){
        aiModel = item.getValue();
      }
    }

    let business;
    for (let item of form?.promptTypes?.current){
      if (item.getValue() !== null){ //null means they chose any in the sidebar options
        business = item.getValue();
      }
    }
    if (business === "business"){
      business = true;
    } else if (business === "regular"){
      business = false;
    }

    //save query parameters
    //isready prevents an infinite loop with the useEffect that sets some form values
    if (router.isReady){
      router.replace({
        query: {
          ...router.query,
          aiModel: aiModel || "",
          query: form.query?.current?.getValue() || router.query.query || "",
          business,
          tags: JSON.stringify(form.tags?.current?.getValue()),
        },
      }, undefined, { scroll: false });
    }

    sendDatalayerEvent("search", "DL - Search", {query: form.query?.current?.getValue() || ""});

    let res = await strapi.query(strapi.endpoints.LISTINGS_SEARCH, {
      data: {
        query: form.query?.current?.getValue() || "",
        tags: form.tags?.current?.getValue()?.map((tag) => tag.label),
        aiModel: aiModel,
        business,
      },
      pagination: {
        page: newPage,
        pageSize: 12,
      },
    });
    if (res.listings){
      setPage(newPage);
      if (newPage === 0){
        setListings(res.listings);
      } else {
        setListings([...listings, ...res.listings]);
      }
    } else {
      setListings(initialListings);
    }

    //no more listings to load
    if (!res.listings || res.listings.length < 12){
      setPage(-1);
    }
  }

  //load any query parameters that were saved
  useEffect(() => {
    if (router?.query){
      form.query.current.setValue(router?.query?.query || "");
      form.tags.current.setValue(JSON.parse(router.query.tags || "[]"));
    }
  }, [router.isReady]);


  return <DefaultLayout
    title="Listings | Lazy Prompts"
    description="Find an expert engineered AI prompt."
    mainClassName={css.main}
    light
    canonical="https://lazyprompts.com/listings"
  >
    <section className={`light ${css.results}`}>
      <div className="sectionInner">
        <aside>
          <div className={css.sidebarSection}>
            <div className={css.label}>Prompt Type</div>
            <div className={css.options}>
              {router.isReady && <>
                <RadioButton
                  defaultChecked={router?.query?.business === "" || router?.query?.business === undefined}
                  name="promptType"
                  value={null}
                  label="Any"
                  onChange={() => updateSearch(0)}
                  ref={(el) => (form.promptTypes.current[0] = el)}
                />
                <hr />
                <RadioButton
                  defaultChecked={router?.query?.business === "false"}
                  name="promptType"
                  value={false}
                  label="Personal"
                  onChange={() => updateSearch(0)}
                  ref={(el) => (form.promptTypes.current[1] = el)}
                />
                <RadioButton
                  defaultChecked={router?.query?.business === "true"}
                  name="promptType"
                  value={true}
                  label="Business"
                  onChange={() => updateSearch(0)}
                  ref={(el) => (form.promptTypes.current[2] = el)}
                />
              </>}
            </div>
          </div>
          <div className={css.sidebarSection}>
            <div className={css.label}>AI Model</div>
            <div className={css.options}>

              {router.isReady && <>
                <RadioButton
                  defaultChecked={router?.query?.aiModel === "" || router?.query?.aiModel === undefined}
                  name="model"
                  value={null}
                  label="All models"
                  onChange={() => updateSearch(0)}
                  ref={(el) => (form.aiModels.current[0] = el)}
                />
                <hr />
                {aiModels
                  ?.sort((a, b) => (a.name + a.version).localeCompare(b.name + b.version))
                  ?.map((model, idx) => <RadioButton
                    key={model.id}
                    defaultChecked={parseInt(router?.query?.aiModel, 10) === model.id}
                    name="model"
                    value={model.id}
                    label={`${model.name} ${model.version}`}
                    onChange={() => updateSearch(0)}
                    ref={(el) => (form.aiModels.current[idx + 1] = el)}
                  />)}
              </>}
            </div>
          </div>
          <div className={`${css.sidebarSection} ${css.tags}`}>
            <div className={css.label}>Tags</div>
            <Selector
              maxOptions="100"
              options={tags}
              className={css.tags}
              isMulti
              noOptionsMessage="No tags found."
              placeholder="Type to search tags"
              ref={form.tags}
              onChange={() => updateSearch(0)}
              validators={[{ test: (val) => true, class: "valid", message: "" }]}
            />
          </div>
          <div className={`${css.sidebarSection}`}>
            <div className={css.label}>Lazy Prompts</div>
            <p>AI prompts are the future of business, and Lazy Prompts is here to help you stay ahead of your competition. The prompts we sell cover every use case, from SEO copywriting to social media posting, from AB testing to banner image generation. Buy AI prompts now and start leveraging the transformative power of AI in your business today.</p>
            <p>If you&apos;re an aspiring prompt engineer, Lazy Prompts offers a unique opportunity to put your skills to use and make some money. Tap into the growing trend of AI prompting and sell your AI prompts on Lazy Prompts.</p>
          </div>
        </aside>
        <div className={css.banner}>
          <div className={css.headline}>
            <div>Expert Engineered</div>
            <div>AI Prompts</div>
          </div>
          <div className={css.pitch}>Lazy Prompts is a marketplace for buying and selling AI prompts. Find your next enterprise AI prompt today.</div>
        </div>
        <div className={css.column}>
          <div className={css.search}>
            <SearchBar placeholder="Search prompts..." ref={form.query} onFinish={() => updateSearch(0)} className={css.searchBar} light/>


          </div>
          <div className={css.listings}>
            {listings.map((listing) => <ProductCard
              listing={listing}
              key={listing.id}
              light
            />)}
            {listings.length <= 6 && <div className={css.noListings}>
              {listings.length === 0 && <div className={css.alert}>No listings found</div>}
              <div className={css.pitch}>Looking for something specific? Send us a prompt request!</div>
              <RequestPromptForm initialCta="Request Prompt"/>
            </div>}
            {page >= 0 && <div className={css.more}>
              <Button label="Load More" onClick={() => updateSearch(page + 1)}/>
            </div>}
          </div>

        </div>
      </div>
    </section>


    <section className={`light ${css.newest}`}>
      <div className="sectionInner">
        <h2>Newest Prompts</h2>
        <div className={css.prompts}>
          {newestListings.map((listing) => <ProductCard
            light
            key={listing.id}
            listing={listing}
            sizes="(max-width: 1300px) 50vw, (max-width:650px) 100vw, 25vw"
          />)}
        </div>
      </div>
    </section>

  </DefaultLayout>;
}

export async function getStaticProps() {
  let tags = [];

  let tagsRaw = [];
  let page = 0;
  do {
    tagsRaw = await strapi.query(strapi.endpoints.TAGS_FIND, {
      pagination: {
        page,
        pageSize: 100,
      },
      fields: ["id", "name"],
      populate: "*",
    });
    page ++;

    for (let tag of tagsRaw){
      tags.push({
        label: tag.name,
        value: tag.id,
      });
    }
  } while (tagsRaw?.length > 0);

  let aiModels = (await strapi.query(strapi.endpoints.AI_MODELS_FIND, {
    fields: ["id", "name", "version", "type", "tag"],
  }));

  let initialListings = (await strapi.query(strapi.endpoints.LISTINGS_SEARCH, {
    data: {
      query: "",
      tags: "",
    },
    pagination: {
      page: 0,
      pageSize: 12,
    },
  }))?.listings || [];


  let newestListings = await strapi.query(strapi.endpoints.LISTINGS_FIND, {
    sort: "createdAt:desc",
    populate: {
      coverArt: true,
      aiModel: true,
    },
    pagination: {
      pageSize: 4,
    },
  });


  //user is logged in
  return {
    props: {
      tags, //note that tags is NOT the raw response from the api, but a transformed structure suitable for <Select/>
      aiModels,
      initialListings,
      newestListings,
    },
    revalidate: 60 * 60, //once an hour
  };
}
